import React, { useEffect, useState } from 'react';
import { Building2, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { FaviconUpload } from '../components/FaviconUpload';

const FIELDS = [
  { key: 'name', label: 'Organisation name', placeholder: 'e.g. Springfield Rowing Club Inc.', hint: 'Full legal/display name — used in emails, PDF receipts, the browser tab, and as the header title unless overridden in Site Appearance.' },
  { key: 'short_name', label: 'Short name', placeholder: 'e.g. Springfield Rowing', hint: 'Used where space is tight (PDF receipts, compact headers).' },
  { key: 'tagline', label: 'Tagline / motto', placeholder: 'e.g. Row together, win together.', hint: 'Shown on branded emails and receipts, and under the name in the header unless overridden in Site Appearance.' },
  { key: 'member_noun', label: 'Member noun', placeholder: 'e.g. member', hint: 'What you call a member (member, rower, alumnus…).' },
  { key: 'currency', label: 'Currency', placeholder: 'e.g. AUD', hint: 'ISO currency code used for payments and reports.' },
  { key: 'timezone', label: 'Timezone', placeholder: 'e.g. Australia/Hobart', hint: 'IANA timezone name.' },
  { key: 'contact_email', label: 'Contact email', placeholder: 'e.g. secretary@club.org', hint: 'Public contact address for the organisation.' },
  { key: 'abn', label: 'ABN / registration no.', placeholder: 'Optional', hint: 'Business/registration number (optional).' },
  { key: 'website', label: 'Website (published domain)', placeholder: 'https://www.example.org.au', hint: 'Your public address, e.g. https://www.tas.rae.org.au. Used for links and QR codes in raffle posters, tickets and emails.' },
  { key: 'secretary_name', label: 'Secretary name', placeholder: 'e.g. Mr A. Montgomery', hint: 'Shown on the membership application form.' },
  { key: 'postal_address', label: 'Postal address', placeholder: 'e.g. PO Box 665, Rosny Park 7018', hint: 'Where paper applications and cheques are sent.' },
  { key: 'bank_name', label: 'Bank', placeholder: 'e.g. Bendigo Bank', hint: 'For subscription payments by bank transfer.' },
  { key: 'bank_account_name', label: 'Bank account name', placeholder: 'e.g. ROYAL AUST ENGINEERS ASSOC OF TAS', hint: '' },
  { key: 'bank_bsb', label: 'BSB', placeholder: 'e.g. 633000', hint: '' },
  { key: 'bank_account_number', label: 'Account number', placeholder: 'e.g. 225212703', hint: '' },
  { key: 'payid', label: 'PayID', placeholder: 'e.g. tas@rae.org.au', hint: 'Optional.' },
];

export default function OrganisationTab() {
  const { getOrg, updateOrg, uploadImage } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getOrg().then((d) => setForm(d)).catch(() => setForm({}));
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await updateOrg({ ...form, setup_complete: true });
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('Could not save — please try again.');
    }
    setBusy(false);
  };

  if (!form) return <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>;

  return (
    <div className="max-w-2xl" data-testid="organisation-panel">
      <div className="flex items-center gap-2 mb-1">
        <Building2 size={18} style={{ color: 'var(--maroon)' }} />
        <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Organisation</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>
        These details make the site your own. They flow through to the header, footer, browser tab,
        branded emails, PDF receipts and the Treasurer's report — no code changes needed.
      </p>

      <div className="bg-white rounded-lg shadow-sm border p-5 space-y-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>{f.label}</label>
            <input
              value={form[f.key] || ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              data-testid={`org-${f.key}`}
              className="w-full px-3 py-2 rounded border text-sm"
              style={{ borderColor: 'rgba(0,0,0,0.2)' }}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>{f.hint}</p>
          </div>
        ))}

        <div>
          <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Favicon</label>
          <FaviconUpload
            value={form.favicon_url || ''}
            onChange={(url) => setForm((s) => ({ ...s, favicon_url: url }))}
            upload={uploadImage}
            testid="org-favicon-upload"
          />
          <input
            value={form.favicon_url || ''}
            onChange={(e) => setForm((s) => ({ ...s, favicon_url: e.target.value }))}
            placeholder="…or paste an image URL: https://…/favicon.png"
            data-testid="org-favicon_url"
            className="w-full mt-2 px-3 py-2 rounded border text-sm"
            style={{ borderColor: 'rgba(0,0,0,0.2)' }}
          />
          <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>Icon shown in the browser tab. Upload any image (it's resized to 64×64 PNG) or paste a URL. Leave blank for the default.</p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button onClick={save} disabled={busy || !(form.name || '').trim()} data-testid="org-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded font-title"
            style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: (busy || !(form.name || '').trim()) ? 0.6 : 1 }}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save organisation
          </button>
          {msg && (
            <span className="text-sm inline-flex items-center gap-1" data-testid="org-msg"
              style={{ color: msg === 'Saved' ? '#1a7f37' : 'var(--maroon)' }}>
              {msg === 'Saved' && <CheckCircle2 size={14} />} {msg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
